import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  Chip,
  useTheme,
  Button,
  Alert,
  Avatar,
  Divider,
  Tooltip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  FitnessCenter as FitnessCenterIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  Help as HelpIcon,
  Assignment as AssignmentIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  Accessibility as AccessibilityIcon,
  Psychology as PsychologyIcon,
  Stars as StarsIcon,
  BarChart as BarChartIcon,
  EmojiEvents as TrophyIcon,
} from "@mui/icons-material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend,
} from "recharts";
import MetricCard from "../shared/MetricCard";
import ChartContainer from "../shared/ChartContainer";
import dashboardService from "../../../../../services/dashboardService";
import NewColors from "../../../../../themes/NewColors";

const ActivityModule = ({ organizationId }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  // State for metrics view selection
  const [selectedMetrics, setSelectedMetrics] = useState("workout");

  // React Query for dashboard activity data with 15-minute cache
  const {
    data: activityData,
    isLoading,
    error,
    refetch,
    isRefetching,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["dashboard-activity", organizationId],
    queryFn: async () => {
      try {
        // Use the dedicated dashboardService method with proper authentication
        const result = await dashboardService.getActivityMetrics(
          organizationId,
          true,
        ); // Force refresh to bypass internal cache

        console.log("Activity API response:", result);

        // Check if the API call was successful
        if (!result.success) {
          throw new Error(result.error || "Failed to fetch activity data");
        }

        // Return the data portion of the response
        return result.data;
      } catch (error) {
        console.error("Error fetching activity data:", error);
        throw error;
      }
    },
    enabled: !!organizationId,
    staleTime:            60 * 60 * 1000,
    gcTime:               4 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 3,
  });

  // Prepare chart data
  const performanceDistributionData = useMemo(() => {
    if (!activityData?.performance_distribution) return [];

    const colors = {
      excellent: theme.palette.success.main,
      good_progress: theme.palette.success.light,
      needs_improvement: theme.palette.warning.main,
      no_progress_data: theme.palette.grey[500],
    };

    return Object.entries(activityData.performance_distribution).map(
      ([key, value]) => ({
        name: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        value: value.count,
        percentage: value.percentage,
        color: colors[key] || theme.palette.grey[500],
      }),
    );
  }, [activityData, theme]);

  const activityDistributionData = useMemo(() => {
    if (!activityData?.activity_distribution) return [];

    const colors = {
      highly_active: theme.palette.success.dark,
      active: theme.palette.success.main,
      moderately_active: theme.palette.warning.light,
      lightly_active: theme.palette.warning.main,
      inactive: theme.palette.error.main,
    };

    return Object.entries(activityData.activity_distribution).map(
      ([key, value]) => ({
        name: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
        value: value.count,
        percentage: value.percentage,
        color: colors[key] || theme.palette.grey[500],
      }),
    );
  }, [activityData, theme]);

  const handleRefresh = () => {
    refetch();
  };

  const handleClientClick = (clientId) => {
    navigate(`/home/clients/summary/${clientId}/${organizationId}`);
  };

  const formatLastUpdated = (timestamp) => {
    if (!timestamp) return "";
    return new Date(timestamp).toLocaleTimeString();
  };

  // Function to render selected metrics section
  const renderSelectedMetrics = () => {
    switch (selectedMetrics) {
      case "workout":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Completion Rate"
                value={activityData?.workout_metrics?.avg_completion_rate}
                format="percentage"
                subtitle="exercise completion"
                description="Average percentage of exercises completed across all workouts"
                icon={<SpeedIcon />}
                variant={
                  (activityData?.workout_metrics?.avg_completion_rate || 0) >=
                  70
                    ? "success"
                    : (activityData?.workout_metrics?.avg_completion_rate ||
                          0) >= 40
                      ? "warning"
                      : "error"
                }
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Total Sets"
                value={activityData?.workout_metrics?.avg_total_sets}
                format="number"
                subtitle="sets per client"
                description="Average number of total sets attempted per client"
                icon={<BarChartIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Completed Sets"
                value={activityData?.workout_metrics?.avg_completed_sets}
                format="decimal"
                subtitle="completed per client"
                description="Average number of sets actually completed per client"
                icon={<CheckIcon />}
                variant="success"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <MetricCard
                title="Avg Workout Days"
                value={activityData?.workout_metrics?.avg_workout_days}
                format="decimal"
                subtitle="days per client"
                description="Average number of workout days per client"
                icon={<TimerIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
          </Grid>
        );

      case "volume":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Avg Total Volume"
                value={activityData?.volume_metrics?.avg_total_volume}
                format="number"
                subtitle="lbs total"
                description="Average total weight volume lifted across all clients"
                icon={<AccessibilityIcon />}
                variant="success"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Avg Volume Per Set"
                value={activityData?.volume_metrics?.avg_volume_per_set}
                format="number"
                subtitle="lbs per set"
                description="Average weight volume per individual set"
                icon={<FitnessCenterIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Avg Completed Volume"
                value={activityData?.volume_metrics?.avg_completed_volume}
                format="number"
                subtitle="lbs completed"
                description="Average weight volume actually completed"
                icon={<CheckIcon />}
                variant="success"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Active Days (30d)"
                value={activityData?.workout_metrics?.avg_active_days_30}
                format="decimal"
                subtitle="days per client"
                description="Average active workout days in last 30 days"
                icon={<TimerIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Workouts Last 7d"
                value={activityData?.workout_metrics?.avg_workouts_last_7}
                format="decimal"
                subtitle="per client"
                description="Average workouts completed in last 7 days"
                icon={<FitnessCenterIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
          </Grid>
        );

      case "engagement":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Avg Unique Exercises"
                value={activityData?.engagement_metrics?.avg_unique_exercises}
                format="decimal"
                subtitle="per client"
                description="Average number of different exercises attempted per client"
                icon={<StarsIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Avg Workout Reports"
                value={activityData?.engagement_metrics?.avg_workout_reports}
                format="decimal"
                subtitle="per client"
                description="Average number of workout reports submitted per client"
                icon={<AssignmentIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Avg Workout Updates"
                value={activityData?.engagement_metrics?.avg_workout_updates}
                format="decimal"
                subtitle="per client"
                description="Average number of workout progress updates"
                icon={<TrendingUpIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Progress Updates"
                value={activityData?.engagement_metrics?.avg_progress_updates}
                format="decimal"
                subtitle="per client"
                description="Average number of progress updates submitted"
                icon={<TrendingUpIcon />}
                variant="success"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Updates Last 7 Days"
                value={activityData?.engagement_metrics?.avg_updates_last_7}
                format="decimal"
                subtitle="per client"
                description="Average number of progress updates in the last 7 days"
                icon={<TrendingUpIcon />}
                variant="success"
                loading={isLoading}
              />
            </Grid>
          </Grid>
        );

      case "performance":
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <MetricCard
                title="Top Performance Score"
                value={activityData?.performance_scoring?.max_performance_score}
                format="decimal"
                subtitle="highest client score"
                description="The highest performance score achieved by any client"
                icon={<TrophyIcon />}
                variant="success"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MetricCard
                title="Average Score"
                value={activityData?.performance_scoring?.avg_performance_score}
                format="decimal"
                subtitle="organization average"
                description="Average performance score across all clients"
                icon={<BarChartIcon />}
                variant="info"
                loading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <MetricCard
                title="Lowest Score"
                value={activityData?.performance_scoring?.min_performance_score}
                format="decimal"
                subtitle="needs attention"
                description="The lowest performance score requiring intervention"
                icon={<WarningIcon />}
                variant="warning"
                loading={isLoading}
              />
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }) => {
    if (percent < 0.05) return null; // Don't show labels for slices < 5%

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill={NewColors.white}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontSize={12}
        fontWeight="bold"
      >
        {(percent * 100).toFixed(0)}%
      </text>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Activity & Engagement Dashboard 🏋️‍♀️
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Monitor client workout performance, activity levels, and engagement
          patterns
        </Typography>
        {dataUpdatedAt && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Last updated: {formatLastUpdated(dataUpdatedAt)}
          </Typography>
        )}
        <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            size="small"
            disabled={isLoading || isRefetching}
            variant="outlined"
          >
            {isRefetching ? "Refreshing..." : "Refresh"}
          </Button>
          {isRefetching && (
            <Chip label="Refreshing..." size="small" color="primary" />
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message || "Failed to load activity data"}
        </Alert>
      )}

      {/* Organization Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Total Clients"
            value={activityData?.organization_summary?.total_clients}
            subtitle={`${activityData?.organization_summary?.active_clients || 0} active`}
            description="Total clients in your organization with activity tracking"
            icon={<PeopleIcon />}
            variant="default"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Activity Health Score"
            value={activityData?.organization_summary?.activity_health_score}
            format="number"
            subtitle="out of 100"
            description="Overall organization activity and engagement health score based on completion rates and consistency"
            icon={<TrendingUpIcon />}
            variant={
              (activityData?.organization_summary?.activity_health_score ||
                0) >= 70
                ? "success"
                : (activityData?.organization_summary?.activity_health_score ||
                      0) >= 40
                  ? "warning"
                  : "error"
            }
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="At Risk Clients"
            value={activityData?.organization_summary?.at_risk_clients}
            subtitle="need attention"
            description="Clients with low completion rates or engagement requiring coach intervention"
            icon={<WarningIcon />}
            variant="warning"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Active Last 7 Days"
            value={activityData?.organization_summary?.clients_active_last_7}
            subtitle="clients worked out"
            description="Number of clients who completed at least one workout in the past week"
            icon={<FitnessCenterIcon />}
            variant="info"
            loading={isLoading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <MetricCard
            title="Avg Performance Score"
            value={activityData?.performance_scoring?.avg_performance_score}
            format="decimal"
            subtitle="out of 100"
            description="Average performance score across all clients based on completion rates, activity frequency, and consistency"
            icon={<AssignmentIcon />}
            variant="info"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* Detailed Metrics Section with Selector */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              📊 Detailed Metrics
            </Typography>
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel id="metrics-select-label">View Metrics</InputLabel>
              <Select
                labelId="metrics-select-label"
                value={selectedMetrics}
                onChange={(e) => setSelectedMetrics(e.target.value)}
                label="View Metrics"
                size="small"
              >
                <MenuItem value="workout">📊 Workout Metrics</MenuItem>
                <MenuItem value="volume">💪 Volume & Activity</MenuItem>
                <MenuItem value="engagement">📈 Engagement</MenuItem>
                <MenuItem value="performance">🏆 Performance Scoring</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {/* Dynamic metrics content based on selection */}
        <Grid item xs={12}>
          {renderSelectedMetrics()}
        </Grid>
      </Grid>

      {/* Actionable Insights - Increased Height */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12}>
          <ChartContainer
            title="🎯 Actionable Insights"
            subtitle={`${activityData?.actionable_insights?.alerts || 0} alerts requiring attention`}
            description="Key actions to improve client engagement and performance"
            loading={isLoading}
            height={500}
            actions={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Chip
                  label={`${activityData?.actionable_insights?.alerts || 0} Alerts`}
                  color="warning"
                  size="small"
                />
                <Chip
                  label={`${activityData?.actionable_insights?.top_performers || 0} Top Performers`}
                  color="success"
                  size="small"
                />
              </Box>
            }
          >
            <List
              sx={{
                p: 2,
                pt: 0,
                height: "100%",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: 8,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  borderRadius: 4,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                  borderRadius: 4,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.5)",
                  },
                },
              }}
            >
              {activityData?.actionable_insights?.priority_actions?.map(
                (action, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: 1,
                      borderColor: "warning.light",
                      borderRadius: 2,
                      mb: 2,
                      backgroundColor: "rgba(255, 152, 0, 0.08)",
                      padding: 3,
                      "&:hover": {
                        backgroundColor: "rgba(255, 152, 0, 0.12)",
                      },
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 3,
                        bgcolor: "warning.main",
                        width: 48,
                        height: 48,
                      }}
                    >
                      <PsychologyIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Priority Action #{index + 1}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.6, color: "text.secondary" }}
                      >
                        {action}
                      </Typography>
                    </Box>
                    <Chip
                      label="Action Required"
                      color="warning"
                      size="small"
                      variant="outlined"
                    />
                  </ListItem>
                ),
              ) || []}

              {(!activityData?.actionable_insights?.priority_actions ||
                activityData.actionable_insights.priority_actions.length ===
                  0) &&
                !isLoading && (
                  <Box sx={{ textAlign: "center", py: 8 }}>
                    <CheckIcon
                      sx={{ fontSize: 64, color: "success.main", mb: 3 }}
                    />
                    <Typography
                      variant="h5"
                      color="success.main"
                      sx={{ mb: 2 }}
                    >
                      All Good!
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      No immediate actions required. Your clients are performing
                      well!
                    </Typography>
                  </Box>
                )}
            </List>
          </ChartContainer>
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Performance Distribution Chart */}
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Performance Distribution"
            subtitle="Client performance tier breakdown"
            description={activityData?.classification_guide?.performance_tier}
            loading={isLoading}
            height={400}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={performanceDistributionData}
                  cx="50%"
                  cy="45%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={110}
                  fill={NewColors.active.primary}
                  dataKey="value"
                >
                  {performanceDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value, name) => [
                    `${value} clients (${performanceDistributionData.find((d) => d.name === name)?.percentage}%)`,
                    name,
                  ]}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>

        {/* Activity Distribution Chart */}
        <Grid item xs={12} md={6}>
          <ChartContainer
            title="Activity Level Distribution"
            subtitle="Client activity frequency breakdown"
            description={activityData?.classification_guide?.activity_level}
            loading={isLoading}
            height={400}
          >
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={activityDistributionData}
                  cx="50%"
                  cy="45%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={110}
                  fill={NewColors.active.primary}
                  dataKey="value"
                >
                  {activityDistributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(value, name) => [
                    `${value} clients (${activityDistributionData.find((d) => d.name === name)?.percentage}%)`,
                    name,
                  ]}
                />
                <Legend verticalAlign="bottom" height={36} />
              </PieChart>
            </ResponsiveContainer>
          </ChartContainer>
        </Grid>
      </Grid>

      {/* At Risk Clients List */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ChartContainer
            title="At Risk Clients"
            subtitle={`${activityData?.at_risk_clients?.length || 0} clients requiring immediate attention`}
            description="Clients with low exercise completion rates who need coaching intervention"
            loading={isLoading}
            height={600}
            actions={
              <Tooltip
                title={
                  activityData?.performance_score_guide?.description ||
                  "Performance scoring information"
                }
              >
                <IconButton size="small">
                  <HelpIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            }
          >
            <List
              sx={{
                p: 0,
                maxHeight: "calc(100% - 40px)",
                overflowY: "auto",
                "&::-webkit-scrollbar": {
                  width: 6,
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                  borderRadius: 3,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.3)",
                  borderRadius: 3,
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.5)",
                  },
                },
              }}
            >
              {activityData?.at_risk_clients?.map((client) => (
                <ListItem
                  key={client.client_id}
                  sx={{
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1.5,
                    flexDirection: "column",
                    alignItems: "stretch",
                    backgroundColor: "background.paper",
                    "&:hover": {
                      backgroundColor: "action.hover",
                      cursor: "pointer",
                    },
                  }}
                  onClick={() => handleClientClick(client.client_id)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      mb: 1,
                    }}
                  >
                    <Avatar
                      sx={{
                        mr: 2,
                        bgcolor: theme.palette.warning.main,
                      }}
                    >
                      <PersonIcon />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        {client.client_first_name} {client.client_last_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {client.risk_reason}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Chip
                        label={`Priority ${client.priority_level}`}
                        color="warning"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        Rank #{client.client_rank}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Client metrics */}
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="primary">
                          {client.performance_score?.toFixed(1) || 0}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Performance Score
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6" color="error">
                          {Math.round(client.exercise_completion_percentage)}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Completion Rate
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h6">
                          {client.workouts_last_7_days}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Workouts (7d)
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={3}>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          color={
                            client.days_since_last_workout > 7
                              ? "error"
                              : "text.primary"
                          }
                        >
                          {client.days_since_last_workout}d
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Last Workout
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Additional metrics row */}
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Chip
                          label={client.performance_tier}
                          size="small"
                          color={
                            client.performance_tier === "Excellent"
                              ? "success"
                              : client.performance_tier === "Good Progress"
                                ? "info"
                                : client.performance_tier ===
                                    "Needs Improvement"
                                  ? "warning"
                                  : "default"
                          }
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Chip
                          label={client.activity_level}
                          size="small"
                          color={
                            client.activity_level === "Highly Active"
                              ? "success"
                              : client.activity_level === "Active"
                                ? "info"
                                : "default"
                          }
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box sx={{ textAlign: "center" }}>
                        <Chip
                          label={client.progress_trend}
                          size="small"
                          color={
                            client.progress_trend === "Improving"
                              ? "success"
                              : "default"
                          }
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </ListItem>
              ))}

              {(!activityData?.at_risk_clients ||
                activityData.at_risk_clients.length === 0) &&
                !isLoading && (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <CheckIcon
                      sx={{ fontSize: 48, color: "success.main", mb: 2 }}
                    />
                    <Typography variant="h6" color="success.main">
                      No At-Risk Clients
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All clients are maintaining good activity levels!
                    </Typography>
                  </Box>
                )}
            </List>
          </ChartContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ActivityModule;
